import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, HostListener, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { CompanyService } from '../../../../core/services/api-services/company.service';
import { MobileUserService } from '../../../../core/services/api-services/mobile-user-service';
import { AuthService } from '../../../../core/services/client-services/auth.service';
import { BreadcrumbComponent, BreadcrumbItem } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../../shared/components/breadcrumb/service/breadcrumb.service';
import { FilterPanelComponent } from "../../../../shared/components/filter-panel/filter-panel.component";
import { FilterField, FilterResult } from '../../../../shared/components/filter-panel/model/filter-field.model';
import { PageEvent, PaginatorComponent } from "../../../../shared/components/paginator/paginator.component";
declare var bootstrap: any;

interface MobileUserView {
  address: string | null;
  cid: number;
  city: string | null;
  cixorReferenceNumber: string | null;
  companyId: number | null;
  companyName: string | null;
  companyTradingName: string | null;
  country: string | null;
  createdAt: string;
  district: string | null;
  dob: string | null;
  email: string | null;
  firstName: string;
  isVerified: boolean | null;
  isWageAccessAllowed: boolean;
  lastName: string | null;
  middleName: string | null;
  mobileNumber: string | null;
  province: string | null;
  updatedAt: string;
  walletLimit: number | null;
  linkStatus: boolean;
  isUserActive: boolean;
  blockReason: string | null;
  userType: string | null;
  totalBasic: number | null;
  totalDeduction: number | null;
  totalallowance: number | null;
  payDayEligible: boolean;
  perDayAmount: number | null;
  earnWageWithdrawalAllowPercentage: number | null;
  earnWageWithdrawalStartingDate: string | null;
  salaryCycleEndDate: string | null;
  salaryCycleStartDate: string | null;
}

@Component({
  selector: 'app-cixor-mobile-user-management',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginatorComponent, BreadcrumbComponent, ReactiveFormsModule, FilterPanelComponent],
  templateUrl: './cixor-mobile-user-management.component.html',
  styleUrl: './cixor-mobile-user-management.component.css'
})
export class CixorMobileUserManagementComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  @ViewChild('userViewOffcanvasEl') offcanvasElementRef!: ElementRef;
  // Change from string ref to component type — more reliable
  @ViewChild(FilterPanelComponent) filterPanel!: FilterPanelComponent;

  mobileUsers: MobileUserView[] = [];
  filteredMobileUsers: MobileUserView[] = [];

  pagedMobileUsers: any[] = [];

  MobileUserPage = 1;
  MobileUserPageSize = 10;
  MobileUserPageSizeOptions = [5, 10, 25, 50];

  isLoading = false;
  error = '';
  searchText: string = '';
  selectedUser: MobileUserView | null = null;

  dataSubscriber$!: Subscription;

  // For server-side pagination
  paginatedMobileUsers: any[] = [];
  totalMobileUsers: number = 0;
  currentPage: number = 1;
  pageSize: number = 10;

  // ── Top filter bar ───────────────────────────────────────────────────────
  /** List of companies for the Employer dropdown */
  companies: any[] = [];
  isCompaniesLoading = false;

  /** Bound to [(ngModel)] on each top-filter control */
  filterCompanyId: number | string = '';  // '' means "no filter", otherwise it's a company ID
  filterUserType: string = '';   // '' | 'Linked' | 'Unlinked'
  filterStartDate: string = '';  // 'YYYY-MM-DD'
  filterEndDate: string = '';    // 'YYYY-MM-DD'

  /** True when at least one top-filter has a non-default value */
  get isTopFilterApplied(): boolean {
    return !!(this.filterCompanyId != '' || this.filterUserType || this.filterStartDate || this.filterEndDate);
  }

  set isTopFilterApplied(val: boolean) {
    if (!val) {
      this.filterCompanyId = '';
      this.filterUserType = '';
      this.filterStartDate = '';
      this.filterEndDate = this.todayStr;
      this.currentPage = 1;
    }
  }

  /** Today's date string used as [max] on both date inputs */
  readonly todayStr: string = new Date().toISOString().split('T')[0];

  // Signals
  filterCompanySearchTerm = signal('');
  companyDropdownOpen = false;

  // =============== Server filter Props =============== //

  filterFields: FilterField[] = [
    {
      type: 'radio-group',
      controlName: 'filterType',
      label: 'Filter By:',
      defaultValue: 'CID',
      options: [
        {
          value: 'CID',
          label: 'CID (CIXOR ID)',
          field: {
            type: 'text',
            controlName: 'CID',            // key sent to backend
            placeholder: 'Enter CIXOR ID',
            hint: 'Example: 4340725617 (10 digits)',
            validators: [Validators.required, Validators.pattern(/^\d{10}$/), Validators.minLength(10), Validators.maxLength(10)],
          },
        },
        {
          value: 'MobileNo',
          label: 'Mobile Number',
          field: {
            type: 'tel',
            controlName: 'MobileNo',       // key sent to backend
            placeholder: 'Enter Mobile Number',
            hint: 'Example: +94771234567 or 0771234567',
            validators: [Validators.required, Validators.pattern(/^(\+94|0)?7\d{8}$/), Validators.minLength(10), Validators.maxLength(12)],
          },
        },
      ],
    },
  ];

  // =================================================== //

  quickReasons: string[] = ['Policy violation', 'Suspicious activity', 'Inactive account', 'Requested by employer'];

  constructor(
    private toaster: ToastrService,
    private mobileUserService: MobileUserService,
    private breadcrumbService: BreadcrumbService,
    private formBuilder: FormBuilder,
    private companyService: CompanyService,
    private authService: AuthService
  ) {
    this.dataSubscriber$ = new Subscription();
  }

  ngOnInit(): void {
    // Subscribe to breadcrumb changes
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.breadcrumbItems = items;
      });

    // Set breadcrumbs for this page
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Client', icon: 'bi bi-people' },
      { label: 'Mobile Users', icon: 'bi bi-phone' }
    ]);

    this.filterEndDate = this.todayStr;

    // Load companies for the top-filter dropdown
    this.loadCompanies();

    // Initial data load
    this.fetchMobileUsers(this.currentPage, this.pageSize, this.filterCompanyId, this.filterUserType, this.filterStartDate, this.filterEndDate);
  }

  // ── Company loader ───────────────────────────────────────────────────────

  private loadCompanies(): void {
    this.isCompaniesLoading = true;
    this.dataSubscriber$.add(
      this.companyService.getExistingCompanies().subscribe({
        next: (response: any) => {
          if (response?.success) {
            this.companies = (response.data ?? []) as any[];
          } else {
            this.companies = [];
          }
          this.isCompaniesLoading = false;
        },
        error: () => {
          this.companies = [];
          this.isCompaniesLoading = false;
        }
      })
    );
  }

  // Computed filtered list
  filteredCompanies = computed(() => {
    const term = this.filterCompanySearchTerm().toLowerCase().trim();
    if (!term) return this.companies;
    return this.companies.filter(c =>
      c.companyName.toLowerCase().includes(term) ||
      String(c.companyId).includes(term)
    );
  });

  // Get display name for selected company
  getSelectedCompanyName(): string {
    if (!this.filterCompanyId) return '';
    const found = this.companies.find(c => c.companyId === this.filterCompanyId);
    return found ? `${found.companyName} (${found.companyId})` : '';
  }

  // Open dropdown
  openCompanyDropdown() {
    this.companyDropdownOpen = true;
    this.filterCompanySearchTerm.set('');
  }

  // Handle search input
  onFilterCompanySearchChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.filterCompanySearchTerm.set(input.value);
    this.companyDropdownOpen = true;
  }

  // Select a company (null = "All Employers")
  selectCompany(company: any) {
    this.filterCompanyId = company ? company.companyId : '';
    this.filterCompanySearchTerm.set('');
    this.companyDropdownOpen = false;
    this.onTopFilterChange();
  }

  // Blur handler
  onCompanyBlur() {
    setTimeout(() => {
      this.companyDropdownOpen = false;
      this.filterCompanySearchTerm.set('');
    }, 200);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.searchable-dropdown')) {
      this.companyDropdownOpen = false;
      this.filterCompanySearchTerm.set('');
    }
  }

  // ── Top filter bar ───────────────────────────────────────────────────────

  /** Called whenever any top-filter control changes. */
  onTopFilterChange(): void {
    // Validate date range
    if (this.filterStartDate && this.filterEndDate && this.filterEndDate < this.filterStartDate) {
      this.toaster.warning('End date cannot be before start date', 'Date Range');
      this.filterEndDate = '';
      return;
    }

    this.currentPage = 1;
    this.searchText = '';
    this.fetchMobileUsers(this.currentPage, this.pageSize, this.filterCompanyId, this.filterUserType, this.filterStartDate, this.filterEndDate);
  }

  /** Clears all top-filter values and reloads the default list. */
  resetTopFilter(): void {
    // Reset top-bar filters
    this.filterCompanyId = '';
    this.filterUserType = '';
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.error = '';
    this.isTopFilterApplied = false;
    this.currentPage = 1;

    if (this.filterPanel.isFilterApplied) {
      this.filterPanel.reset();
    }
    // Single fetch — always
    this.fetchMobileUsers(this.currentPage, this.pageSize);
  }

  // ── Data fetch (extended to include top-filter params) ───────────────────
  fetchMobileUsers(page: number, pageSize: number, filterCompanyId?: number | string, filterUserType?: string | null, filterStartDate?: string | null, filterEndDate?: string | null): void {
    this.isLoading = true;

    this.dataSubscriber$.add(
      this.mobileUserService.getAllMobileUsersPaginated(page, pageSize, filterCompanyId, filterUserType, filterStartDate, filterEndDate).subscribe({
        next: (response) => {
          if (response.success) {
            this.paginatedMobileUsers = response.data;
            this.filteredMobileUsers = [...this.paginatedMobileUsers];
            this.totalMobileUsers = response.totalCount;
            this.currentPage = response.pageNumber;
            this.pageSize = response.pageSize;
            this.error = '';
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.paginatedMobileUsers = [];
          this.filteredMobileUsers = [];
          this.error = 'API error: ' + err.message;
          this.isLoading = false;
          this.toaster.error(err?.error?.message || err?.message || 'Failed to load mobile users', 'Error');
        }
      })
    );
  }

  openViewUser(user: MobileUserView): void {
    this.selectedUser = user;
    // Offcanvas is handled by data attributes; this method just sets selectedUser.
  }

  performSearch(): void {
    const q = this.searchText.trim().toLowerCase();
    if (!q) {
      // this.filteredMobileUsers = [...this.mobileUsers];
      this.filteredMobileUsers = [...this.paginatedMobileUsers];
      // this.resetWithdrawalsPagination();
      return;
    }
    // this.filteredMobileUsers = this.mobileUsers.filter(u =>
    this.filteredMobileUsers = this.paginatedMobileUsers.filter(u =>
      Object.values(u).some(v => String(v ?? '').toLowerCase().includes(q))
    );
    // this.resetWithdrawalsPagination();
  }

  closeOffcanvas(): void {
    const offcanvasEl = this.offcanvasElementRef?.nativeElement;
    if (offcanvasEl) {
      const bsOffcanvas = bootstrap.Offcanvas.getInstance(offcanvasEl) || new bootstrap.Offcanvas(offcanvasEl);
      bsOffcanvas.hide();
    }
    // this.resetWithdrawalsPagination();
  }

  // Helpers for display formatting
  formatDate(iso?: string | null): string {
    if (!iso) return '';
    // Expecting "YYYY-MM-DDTHH:mm:ss" -> keep date part
    const d = iso.split('T')[0];
    return d || '';
    // If backend sends "YYYY-MM-DD" already, this still works.
  }

  formatDateTime(date?: string | Date): string {
    if (!date) return '';

    const d = new Date(date);

    const yyyy = d.getFullYear();
    const MM = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12; // convert 0 → 12

    return `${yyyy}-${MM}-${dd}, ${hours}:${minutes}:${seconds} ${ampm}`;
  }

  joinCityDistrict(city?: string | null, district?: string | null): string {
    const parts: string[] = [];
    if (city) parts.push(city);
    if (district) parts.push(district);
    return parts.length ? parts.join(', ') : '—';
  }

  joinProvinceCountry(province?: string | null, country?: string | null): string {
    const parts: string[] = [];
    if (province) parts.push(province);
    if (country) parts.push(country);
    return parts.length ? parts.join(', ') : '—';
  }

  joinLocation = (u: any) => [u.city, u.district, u.province, u.country].filter(x => !!x).join(', ');

  // Build a nice full name
  fullName = (u: any) => [u.firstName, u.middleName, u.lastName].filter(x => !!x).join(' ');

  // Initials for avatar (e.g., "TT")
  initials = (u: any) => {
    const a = (u.firstName || '').trim();
    const b = (u.lastName || '').trim();
    return ((a[0] || '') + (b[0] || '')).toUpperCase() || 'U';
  };

  joinAddressRegion(city?: string | null, district?: string | null, province?: string | null, country?: string | null): string {
    const parts: string[] = [];
    if (city && city != 'N/A') parts.push(city);
    if (district && district != 'N/A') parts.push(district);
    if (province && province != 'N/A') parts.push(province);
    if (country && country != 'N/A') parts.push(country);
    return parts.join(', ');
  }

  toStr(val: unknown): string {
    return val === null || val === undefined ? '' : String(val);
  }

  async copy(text: string) {
    const value = (text ?? '').toString().trim();
    if (!value) {
      this.toaster.info('Nothing to copy', 'Copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      this.toaster.success('Copied to clipboard', 'Copy');
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        this.toaster.success('Copied to clipboard', 'Copy');
      } catch {
        this.toaster.error('Copy failed', 'Copy');
      } finally {
        document.body.removeChild(ta);
      }
    }
  }

  private updateWithdrawalsPage(): void {
    if (this.MobileUserPage < 1) this.MobileUserPage = 1;
    const start = (this.MobileUserPage - 1) * this.MobileUserPageSize;
    // this.pagedMobileUsers = this.filteredMobileUsers.slice(start, start + this.MobileUserPageSize);
    this.paginatedMobileUsers = this.filteredMobileUsers.slice(start, start + this.MobileUserPageSize);
  }

  private resetWithdrawalsPagination(): void {
    this.MobileUserPage = 1;
    this.updateWithdrawalsPage();
  }

  // ── Pagination ───────────────────────────────────────────────────────────
  onMobileUserPageChange(event: PageEvent): void {
    this.currentPage = event.page;
    this.pageSize = event.pageSize;

    this.fetchMobileUsers(this.currentPage, this.pageSize, this.filterCompanyId, this.filterUserType, this.filterStartDate, this.filterEndDate);
  }

  // =============================================
  // Server-Side Filter Methods
  // =============================================

  fetchMobileUsersWithFilter(
    page: number,
    pageSize: number,
    filterType: string,
    filterValue: string
  ): void {
    this.isLoading = true;
    this.dataSubscriber$.add(
      this.mobileUserService.searchMobileUsers(page, pageSize, filterType, filterValue).subscribe({
        next: (response) => {
          if (response.success) {
            this.paginatedMobileUsers = response.data;
            this.filteredMobileUsers = [...this.paginatedMobileUsers]; // ← Always sync
            this.totalMobileUsers = response.totalCount;
            this.currentPage = response.pageNumber;
            this.pageSize = response.pageSize;
            this.error = '';

            if (this.paginatedMobileUsers.length === 0) {
              this.toaster.info('No users found matching your filter', 'Filter');
            } else {
              this.toaster.success(`Found ${this.totalMobileUsers} user(s)`, 'Filter');
            }
          } else {

            this.paginatedMobileUsers = [];
            this.filteredMobileUsers = [];
            this.totalMobileUsers = 0;
            this.error = '';
            this.toaster.info('No users found matching your filter', 'Filter');
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.error = 'Search error: ' + err.message;
          this.paginatedMobileUsers = [];
          this.filteredMobileUsers = [];
          this.totalMobileUsers = 0;
          this.isLoading = false;
          this.toaster.error(err?.error?.message || err?.message || 'Failed to search mobile users', 'Error');
        }
      })
    );
  }

  onFilter(result: FilterResult): void {

    const filterType = result['filterType'].toString();
    const filterValue = result[filterType]?.toString().trim();

    if (!filterValue) {
      this.toaster.warning('Please enter a value to filter', 'Filter');
      return;
    }

    this.currentPage = 1;
    this.searchText = '';

    this.fetchMobileUsersWithFilter(this.currentPage, this.pageSize, filterType, filterValue);
  }

  onClear(): void {
    this.currentPage = 1;
    this.searchText = '';
    this.error = '';
    this.fetchMobileUsers(this.currentPage, this.pageSize);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dataSubscriber$.unsubscribe();
  }

  togglingCids = new Set<number>();

  selectQuickReason(reason: string): void {
    this.blockReasonInput = reason;
    this.blockReasonError = '';
  }

  // Block reason modal state
  blockReasonModalUser: MobileUserView | null = null;
  blockReasonInput = '';
  blockReasonError = '';

  /** Called when admin clicks the toggle. Shows modal if disabling; toggles directly if enabling. */
  toggleUserStatus(user: MobileUserView): void {
    if (this.togglingCids.has(user.cid)) return;

    if (user.isUserActive) {
      // Disabling → require a block reason first
      this.blockReasonModalUser = user;
      this.blockReasonInput = '';
      this.blockReasonError = '';
    } else {
      // Enabling → no reason needed, proceed directly
      this._executeToggle(user, undefined);
    }
  }

  confirmDisable(): void {
    const reason = this.blockReasonInput.trim();
    if (!reason) {
      this.blockReasonError = 'Block reason is required.';
      return;
    }
    if (reason.length > 50) {
      this.blockReasonError = 'Block reason must be 50 characters or less.';
      return;
    }
    const user = this.blockReasonModalUser!;
    this.blockReasonModalUser = null;
    this._executeToggle(user, reason);
  }

  cancelDisable(): void {
    this.blockReasonModalUser = null;
    this.blockReasonInput = '';
    this.blockReasonError = '';
  }

  private _executeToggle(user: MobileUserView, blockReason: string | undefined): void {
    const performedBy = this.authService.getUser()?.portalAdminId ?? 0;
    this.togglingCids.add(user.cid);

    this.mobileUserService.toggleMobileUserStatus(user.cid, performedBy, blockReason).subscribe({
      next: (res: any) => {
        if (res?.success) {
          user.isUserActive = res.data as boolean;
          if (!user.isUserActive) user.blockReason = blockReason ?? null;
          else user.blockReason = null;
          this.toaster.success(res.message ?? 'User status updated.', 'Success');
        } else {
          this.toaster.error(res?.message ?? 'Failed to update user status.', 'Error');
        }
        this.togglingCids.delete(user.cid);
      },
      error: () => {
        this.toaster.error('Failed to update user status.', 'Error');
        this.togglingCids.delete(user.cid);
      }
    });
  }

}
